import { Injectable } from '@angular/core';
import { StatusCard } from './models/status-card';
import { States, MerchantConfig, Application } from 'app/application/application';
import { CardType } from './models/card-type.enum';
import { Progress } from './models/progress';
import { PostAccept } from 'app/risk/risk';
import { environment } from 'environments/environment';
import { LoanCard } from './models/loan-card';
import { RupeeCurrencyPipe } from 'app/common/pipes';
import { TokenService } from 'app/token/token.service';
import { ApplicationService } from 'app/application/application.service';
import { JourneyFlowType } from 'app/token/token';

@Injectable()
export class DashboardService {

  constructor(
    private rupeePipe: RupeeCurrencyPipe,
    private applicationService: ApplicationService,
    private tokenService: TokenService) { }

  /**
   * Get Progress state based on current preapprove states
   * @param {States} states The users preapprove states
   * @param {PostAccept} clState The users credit limit state
   * @param {MerchantConfig} merchantConfig The current merchant Configuration
   * @return {StatusCard} The current progress state card
   */
  getCLStatus(states: States, clReactivationEnabled = true, clState: PostAccept, merchantConfig: MerchantConfig, pauseJourney: boolean = false, riskStatus: any): StatusCard {
    const flipkartUltraFlowEnabled = this.tokenService.getFlipkartUltraFlag();
    const r1FastlaneApproved = (clState && clState.credit_limit && clState.credit_limit.type && clState.credit_limit.type === 6) ? true : false;
    const bdPostKycFlow = this.tokenService.getBdPostKycFlow();
    const idffFlow = this.tokenService.getJourneyFlowType() === JourneyFlowType.Idff;
    let r1RejectionStatus = false;
    let r2RejectionStatus = false;
    let r3RejectionStatus = false;
    let statusCard: StatusCard;
    let kycSkip = this.tokenService.getKycSkip();

    if (riskStatus && riskStatus instanceof Array) {
      // Sort the risk status based on created_at desc
     const riskStatusSortedByCreatedAtDesc =  riskStatus.sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      const lastRiskStatus = riskStatusSortedByCreatedAtDesc[0];

      if (lastRiskStatus && lastRiskStatus.stage === 'CLAllocationStage1' && lastRiskStatus.decision_code === 3) {
        r1RejectionStatus = true;
      } else if (lastRiskStatus && lastRiskStatus.stage === 'CLAllocationStage2' && lastRiskStatus.decision_code === 3) {
        r2RejectionStatus = true;
      } else if (lastRiskStatus && lastRiskStatus.stage === 'CLAllocationStage3' && lastRiskStatus.decision_code === 3) {
        r3RejectionStatus = true;
      }
    }

    if (kycSkip && kycSkip.skip) {
      kycSkip = (kycSkip.version === '3.13') ? ((states.customer.proofOfIdUploaded && states.customer.proofOfAddressUpload) || states.customer.kycFlowCompleted) : states.customer.kycFlowCompleted;
    } else {
      kycSkip = (states.customer.proofOfIdUploaded && states.customer.proofOfAddressUpload);
    }
    if (Object.values(environment.offlineCustomMerchants).indexOf(this.applicationService.getMerchantId()) > -1) {
      const merchant = this.applicationService.getApplication() ? this.applicationService.getApplication().MerchantName.toLowerCase() : '';
      if (!states.loan.IsDownpaymentComplete && states.loan.KycFlowCompleted) {
        statusCard = new StatusCard(
          `r2_approved_kyc_done_${merchant}`,
          CardType.Approved,
          new Progress(
            'kyc',
            '',
            'Your application has been received and is under review. You will get a SMS with further instructions.',
            '2 steps away from shopping your favourite products',
            6,
            'continue'
          ),
          clState ? clState.credit_limit.available_amt : 0
        );
      }
    } else {
      if (states) {
        if (clState && ['ZMRSK704', 'ZMRSK946', 'ZMRSK947'].indexOf(clState.status_code) !== -1) {
          const clStateMsg = {
            ZMRSK704: 'Please check again after few days.',
            ZMRSK946: 'Thank you for choosing ZestMoney! We think a loan at this moment could add to your financial burden. Please try availing a loan in a few weeks. Sorry for the inconvenience.',
            ZMRSK947: 'Thank you for choosing ZestMoney! We see you have an on-going EMI with us and another loan at this time could be a financial burden. Please repay your existing EMIs before making your next purchase using ZestMoney.',
          };
          statusCard = new StatusCard(
            'cl-expired',
            CardType.Blocked,
            new Progress(
              'cl-expired',
              'Your credit limit is blocked',
              clStateMsg[clState.status_code],
              '',
              0,
              ''
            ),
            clState.credit_limit.available_amt
          );
        } else if (clState && clState.status_code === 'ZMRSK408') {
          if (pauseJourney) {
            statusCard = new StatusCard(
              'cl_pause',
              CardType.Paused
            );
          } else {
            statusCard = new StatusCard(
              'cl_expired',
              CardType.Expired,
              new Progress(
                'cl-expired',
                'Your Credit Limit is on hold',
                clReactivationEnabled ? 'Due to an inactivity for a long time, your credit limit is on hold' : 'Due to an inactivity for a long time, your credit limit is on hold. Please check back again in few days to reactivate.',
                '',
                0,
                clReactivationEnabled ? 'reactivate' : null
              ),
              clState.credit_limit.available_amt
            );
          }
        } else if ( r3RejectionStatus ) {
          statusCard = new StatusCard(
            'r3_declined',
            CardType.Declined
          );
        } else if (states.customer.riskStage1Status === false || r1RejectionStatus) {
          statusCard = new StatusCard(
            'r1_declined',
            CardType.Declined
          );
        } else if (states.customer.riskStage2Status === 'Declined' || r2RejectionStatus) {
          statusCard = new StatusCard(
            'r2_declined',
            CardType.Declined
          );
        } else if (states.customer.creditLimitActive && (states.customer.isMandateCompleted || merchantConfig.skipMandatePage)) {
          if (clState && clState.credit_limit && clState.credit_limit.available_amt === 0) {
            if (flipkartUltraFlowEnabled) {
              statusCard = new StatusCard(
                'cl_empty',
                CardType.Info,
                new Progress(
                  'cl-expired',
                  'No credit limit available',
                  'Repay your EMIs to get more credit limit',
                  '',
                  0
                )
              );
            } else {
              statusCard = new StatusCard(
                'cl_empty',
                CardType.Info,
                new Progress(
                  'cl-expired',
                  'No credit limit available',
                  'Repay your EMIs to get more credit limit',
                  '',
                  0,
                  'manage emis'
                )
              );
            }
          } else if (merchantConfig.offlineGC) {
            statusCard = new StatusCard(
              'cl_active',
              CardType.Approved,
              new Progress(
                'cl-approved',
                'Your credit limit is active',
                'Now, enjoy the convenience of EMIs across your favourite stores'
              ),
              clState ? clState.credit_limit.available_amt : 0
            );
          } else if (merchantConfig.merchantId === environment.zestMerchantId) {
            statusCard = new StatusCard(
              'cl_active',
              CardType.Approved,
              new Progress(
                'cl-approved',
                'Your credit limit is active',
                'Shop your heart out and pay in EMIs with ZestMoney',
                '',
                0,
                'shop now'
              ),
              clState ? clState.credit_limit.available_amt : 0
            );
          } else if (flipkartUltraFlowEnabled) {
            statusCard = new StatusCard(
              'cl_active_flipkartultra',
              CardType.Approved,
              new Progress(
                'cl-approved',
                'Your credit limit is active',
                'Shop your heart out and pay in EMIs with ZestMoney',
                '',
                0,
                ''
              ),
              clState ? clState.credit_limit.available_amt : 0
            );
          } else {
            statusCard = new StatusCard(
              'cl_active',
              CardType.Approved,
              new Progress(
                'cl-approved',
                'Your credit limit is active'
              ),
              clState ? clState.credit_limit.available_amt : 0
            );
          }
        } else if ((states.customer.riskStage2Status === 'Approved' || states.customer.riskStage2Status === 'Accepted' || states.customer.riskStage2Status === 'Referred') && kycSkip) {
          if (!states.customer.isMandateCompleted || states.customer.isMandateCollectedOffline) {
            if (merchantConfig.skipMandatePage) {
              statusCard = new StatusCard(
                'r2_approved_skip_mandate',
                CardType.Approved,
                new Progress(
                  'nach',
                  'Your credit limit is approved'
                ),
                clState ? clState.credit_limit.available_amt : 0
              );
            } else {
              statusCard = new StatusCard(
                'r2_approved_kyc_done',
                CardType.Approved,
                new Progress(
                  'nach',
                  'Your credit limit is approved',
                  'To start using, activate it by setting up auto repayment',
                  '1 step away from shopping your favourite products',
                  8,
                  'activate'
                ),
                clState ? clState.credit_limit.available_amt : 0
              );
            }
          } else if (merchantConfig.offlineGC) {
            statusCard = new StatusCard(
              'r2_approved_nach_referred',
              CardType.Referred,
              new Progress(
                'referred',
                'Yay! Application received',
                'Your application will be verified within 10 minutes and you will get the status via email and SMS',
                '',
                0
              )
            );
          } else {
            statusCard = new StatusCard(
              'r2_approved_mandate_review',
              CardType.Approved,
              new Progress(
                'nach',
                'Your credit limit is approved',
                'Your repayment setup is under review'
              ),
              clState ? clState.credit_limit.available_amt : 0
            );
          }
        } else if (states.customer.riskStage2Status === 'Approved' || states.customer.riskStage2Status === 'Accepted' || states.customer.riskStage2Status === 'Referred') {
          statusCard = new StatusCard(
            'r2_approved',
            CardType.Approved,
            new Progress(
              'kyc',
              'Your credit limit is approved',
              'Use your credit limit by completing your KYC',
              '2 steps away from shopping your favourite products',
              6,
              'complete kyc'
            ),
            clState ? clState.credit_limit.available_amt : 0
          );

        } else if (pauseJourney) {
          statusCard = new StatusCard(
            'cl_pause',
            CardType.Paused
          );
        } else if (states.customer.riskStage1Status && ((states.customer.proofOfIncomeMethod || states.customer.kycFlowCompleted) || !states.customer.requiredPerfios || r1FastlaneApproved)) {
          if (bdPostKycFlow && states.customer.kycFlowCompleted) {
            statusCard = new StatusCard(
              'r2_approved_kyc_done',
              CardType.Approved,
              new Progress(
                'nach',
                'Your credit limit is approved',
                'To start using, activate it by setting up auto repayment',
                '1 step away from shopping your favourite products',
                8,
                'activate'
              ),
              clState ? clState.credit_limit.available_amt : 0
            );
          } else if (bdPostKycFlow) {
            statusCard = new StatusCard(
              'r2_approved',
              CardType.Approved,
              new Progress(
                'kyc',
                'Your credit limit is approved',
                'Use your credit limit by completing your KYC',
                '2 steps away from shopping your favourite products',
                6,
                'complete kyc'
              ),
              clState ? clState.credit_limit.available_amt : 0
            );
          } else {
            const stepsText = states.customer.kycFlowCompleted ? '2 steps away from getting credit upto 1.5 lacs' : '3 steps away from getting credit upto 1.5 lacs';
            const stepsScale = states.customer.kycFlowCompleted ? 6 : 5;
            statusCard = new StatusCard(
              'iv_done',
              CardType.Progress,
              new Progress(
                'iv',
                'Complete Bank Details',
                'Get your credit limit to pay in EMIs across 1000+ partners',
                stepsText,
                stepsScale,
                'continue'
              )
            );
          }
        } else if (states.customer.riskStage1Status) {
          statusCard = new StatusCard(
            'r1_done',
            CardType.Progress,
            new Progress(
              'iv',
              'Verify your Income',
              'Get your credit limit to pay in EMIs across 1000+ partners',
              '3 steps away from getting credit upto 1.5 lacs',
              3,
              'continue'
            )
          );
        } else if (states.customer.isEDCompleted) {
          statusCard = new StatusCard(
            'ed_done',
            CardType.Progress,
            new Progress('iv',
              'Complete your Profile',
              'Get your credit limit to pay in EMIs across 1000+ partners',
              '4 steps away from getting credit upto 1.5 lacs',
              3,
              'continue'
            )
          );
        } else if (states.customer.isPDCompleted) {
          statusCard = new StatusCard(
            'pd_done',
            CardType.Progress,
            new Progress('profile',
              'Complete your Profile',
              'Get your credit limit to pay in EMIs across 1000+ partners',
              '4 steps away from getting credit upto 1.5 lacs',
              2,
              'continue'
            )
          );
        } else if (idffFlow) {
          statusCard = new StatusCard(
            'pd_done',
            CardType.Progress,
            new Progress('profile',
              'Complete your Profile',
              'Get your credit limit to pay in EMIs across 1000+ partners',
              '4 steps away from getting credit upto 1.5 lacs',
              1,
              'continue'
            )
          );
        } else {
          statusCard = new StatusCard(
            'get_started',
            CardType.GetStarted
          );
        }
      } else {
        statusCard = new StatusCard(
          'error',
          CardType.Error,
          new Progress(
            'error',
            'We run into an error',
            'Please try after sometime'
          ));
      }
    }

    return statusCard;
  }

  /**
   * Update loan progress based on current status
   * @param {StatusCard} loanCard The current loan card
   * @param {Application} application The current application
   * @param {States} states The users preapprove states
   * @param {LoanCard} statusCard The current status card
   * @param {string} isCreditLimitCustomer Is the customer a credit limit customer
   * @return {LoanCard} The current progress state card
   */
  updateLoanProgress(loanCard, application, states, isCreditLimitCustomer): LoanCard {
    let productName = 'product';
    let progressMsg = ' away from getting your ' + productName;
    let progressCount = 0;
    let scale = 0;

    if (application.BasketItems.length) {
      productName = application.BasketItems[0].Description;
    }

    if (isCreditLimitCustomer) {
      progressCount = 0;
      scale = 10;
      if (!states.loan.IsLoanAgreementAccepted) {
        progressCount = progressCount + 1;
        scale = scale - 2;
      }
      if (application.IsDpAfterRiskApproval && !states.loan.IsDownpaymentComplete) {
        progressCount = progressCount + 1;
        scale = scale - 2;
      }
      if (!(states.loan.IsMandateCompleted || states.loan.IsMandateCollectedOffline)) {
        progressCount = progressCount + 1;
        scale = scale - 2;
      }
      if (!(states.loan.IsPoiUploaded || states.loan.KycFlowCompleted)) {
        progressCount = progressCount + 1;
        scale = scale - 2;
      }
      if (!states.loan.IsBankAccountDetailsComplete) {
        progressCount = progressCount + 1;
        scale = scale - 2;
      }
    } else if (states.loan.IsRiskAccepted || states.loan.IsRiskRefered) {
      if (states.loan.IsPoiUploaded || states.loan.KycFlowCompleted) {
        progressCount = 1;
        scale = 8;
      } else {
        progressCount = 2;
        scale = 6;
      }
      if (application.IsDpAfterRiskApproval && !states.loan.IsDownpaymentComplete) {
        progressCount = progressCount + 1;
        scale = scale - 2;
      }
    } else if (states.loan.IsBankAccountDetailsComplete && states.loan.IsLoanAgreementAccepted) {
      progressCount = 2;
      scale = 6;
    } else if (states.loan.IsLoanAgreementAccepted) {
      progressCount = 3;
      scale = 4;
    } else if (states.loan.IsPreAccepted) {
      progressCount = 4;
      scale = 2;
    } else {
      progressCount = 5;
      scale = 1;
    }

    progressMsg = progressCount + (progressCount === 1 ? ' step' + progressMsg : ' steps' + progressMsg);
    loanCard = { ...loanCard, progressMsg, scale };

    return loanCard;
  }

  /**
   * Get Progress state based on current preapprove states
   * @param {Application} application The current application
   * @param {States} states The users preapprove states
   * @param {PostAccept} clState The users credit limit state
   * @param {statusCard} StatusCard The current user state card
   * @param {string} [status] Predefined status
   * @return {LoanCard} The current progress state card
   */
  getLoanStatus(application: Application, states: States, clState: PostAccept, statusCard: StatusCard, status?: string): LoanCard {
    const approvedClStatusCodes = ['ZMRSK201', 'ZMRSK202', 'ZMRSK400'];
    const isCreditLimitCustomer = this.applicationService.getCreditLimitCust();
    const isDpAfterRiskApproval = this.applicationService.getDpConfig();

    let loanCard: LoanCard;

    if (status) {
      // Create loan card based on pre-defined status
      switch (status) {
        case 'declined': {
          if (clState && clState.credit_limit && application.LoanAmount > clState.credit_limit.available_amt && clState.status_code && approvedClStatusCodes.indexOf(clState.status_code) > -1) {
            loanCard = new LoanCard(
              'counter_offer',
              CardType.CounterOffer,
              'Voucher amount changed',
              'Continue with your new voucher amount',
              '',
              0,
              0,
              0,
              '',
              0,
              `We couldn\'t approve your application for ${this.rupeePipe.transform(application.LoanAmount)}. However congratulations! You have been approved for a credit of ${this.rupeePipe.transform(clState.credit_limit.available_amt)}.`
            );
          } else {
            loanCard = new LoanCard(
              'loan_declined',
              CardType.Declined,
              ''
            );
          }
          break;
        }
        case 'credit-limit-insufficient': {
          if (clState && ['ZMRSK704', 'ZMRSK946', 'ZMRSK947'].indexOf(clState.status_code) !== -1) {
            loanCard = null;
          } else {
            loanCard = new LoanCard(
              'loan_cl_insufficient',
              CardType.CLInsufficient,
              'Insufficient credit limit',
              `Your order amount of ${this.rupeePipe.transform(application.BasketAmount)} is more than your credit balance of ${this.rupeePipe.transform(clState ? clState.credit_limit.available_amt : 0)}.
               Please try with a lesser amount.`,
              '',
              0,
              0,
              0,
              '',
              0,
              `Please generate a voucher with your available ZestMoney credit limit and enter the voucher code on the merchant’s order page. Pay the balance order amount with your preferred mode of payment (Debit card, credit card, UPI etc.) to complete your purchase.`
            );
          }
          break;
        }
        case 'voucher-generation-failed': {
          loanCard = new LoanCard(
            'voucher-generation-failed',
            CardType.GenerationFailed,
            'Your vouchers are ready',
            'We will email the voucher codes and pin shortly.'
          );
          break;
        }
        case 'voucher-generation': {
          loanCard = new LoanCard(
            'voucher-generation',
            CardType.Generated,
            'Your voucher codes are ready',
            'You will receive them on your email and WhatsApp'
          );
          break;
        }
      }
    } else if (statusCard.cardType !== CardType.Error) {
      if ([2, 3, 5, 11, 12, 15].indexOf(states.loan.CurrentStatus) > -1) {
        loanCard = new LoanCard(
          'loan_declined',
          CardType.Declined,
          ''
        );
      } else if (states.loan.CurrentStatus === 18) {
        loanCard = new LoanCard(
          'loan_out_of_stock',
          CardType.OutOfStock,
          ''
        );
      } else if (states.loan.CurrentStatus === 10) {
        if (states.loan.IsMandateCollectedOffline) {
          loanCard = new LoanCard(
            'loan_approved',
            CardType.Approved,
            'Your voucher is approved',
            'Get your voucher now to start shopping',
            application.MerchantName,
            application.BasketAmount,
            application.NumberOfInstallments,
            application.TotalInstallmentAmount
          );
        } else {
          loanCard = new LoanCard(
            'loan_complete',
            CardType.OrderComplete,
            'Your voucher is approved',
            'Get your voucher now to start shopping',
            application.MerchantName,
            application.BasketAmount,
            application.NumberOfInstallments,
            application.TotalInstallmentAmount,
            '',
            0,
            '',
            'OCDLoanApproved'
          );
        }
      } else if ((states.loan.IsLoanAgreementAccepted
        && (states.loan.IsPoiUploaded || states.loan.KycFlowCompleted)
        && ((states.loan.IsRiskAccepted && states.loan.IsDownpaymentComplete && (states.loan.IsMandateCompleted || states.loan.IsMandateCollectedOffline))
          || (states.loan.IsRiskRefered && (isDpAfterRiskApproval || (states.loan.IsMandateCompleted || states.loan.IsMandateCollectedOffline))))) ||
        (states.loan.IsLoanAgreementAccepted
          && (states.loan.IsPoiUploaded || states.loan.KycFlowCompleted)
          && ((states.loan.IsDownpaymentComplete && (states.loan.IsMandateCompleted || states.loan.IsMandateCollectedOffline))
            || ((isDpAfterRiskApproval || (states.loan.IsMandateCompleted || states.loan.IsMandateCollectedOffline))) && !(states.loan.IsRiskAccepted && states.loan.IsRiskRefered && states.loan.IsRiskDeclined)))) {
        loanCard = new LoanCard(
          'loan_referred',
          CardType.Referred,
          'Your request is being processed',
          'It will be verified within 24 hours and you will get an update via email/SMS'
        );
      } else {
        const lCard = new LoanCard(
          'loan_progress',
          CardType.Progress,
          'Finish voucher checkout',
          '',
          application.MerchantName,
          application.BasketAmount,
          application.NumberOfInstallments,
          application.TotalInstallmentAmount
        );
        loanCard = this.updateLoanProgress(lCard, application, states, isCreditLimitCustomer);
      }
    }
    return loanCard;
  }
}
